import React, { useEffect, useMemo } from "react";
import { getErrorDetails } from "../../../app/appUtils";
import { MySelect } from "../../../components/ui/Select";
import { cn } from "../../../lib/utils";
import { Fine } from "../@types";
import { useGetFinesMutation } from "../fineApiSlice";

interface FineDropdownMenuProps {
  value?: string;
  onChange?: (value: string) => void;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  name?: string;
  id?: string;
  showLabel?: boolean;
  label?: string;
  error?: string;
}

interface FineOption {
  value: string;
  label: string;
}

const FineDropdownMenu: React.FC<FineDropdownMenuProps> = ({
  value,
  onChange,
  className,
  disabled,
  placeholder = "Fines",
  name = "fines",
  id = "fines",
  showLabel = true,
  error,
  label,
}) => {
  const [getFines, { data: fines, isLoading, isError, error: errorData }] =
    useGetFinesMutation();

  const partners = useMemo(() => {
    return fines?.data.data || [];
  }, [fines?.data.data]);

  useEffect(() => {
    getFines({
      pageNumber: 0,
      pageSize: 100,
    });
  }, [getFines]);

  const driversOptions: FineOption[] = useMemo(() => {
    if (isLoading) {
      return [{ value: "", label: "Loading..." }];
    }

    if (isError) {
      return [{ value: "", label: "Error loading fines" }];
    }

    return partners.map((crewMember: Fine) => ({
      value: crewMember.id,
      label: `${crewMember.name} -- ${crewMember.category}`,
    }));
  }, [partners, isLoading, isError]);

  const handleValueChange = (option: FineOption) => {
    onChange?.(option.value);
  };

  // Helper functions for MySelect
  const getValue = (option: FineOption) => option.value;
  const getName = (option: FineOption) => option.label;

  return (
    <div className={cn("w-full")}>
      <MySelect<FineOption>
        name={name}
        id={id}
        label={showLabel ? label || "Fine" : ""}
        options={driversOptions}
        getValue={getValue}
        getName={getName}
        value={value}
        onChange={onChange}
        onValChange={handleValueChange}
        placeholder={placeholder}
        disabled={disabled || isLoading || isError}
        className={cn(
          "focus:ring-primary focus:border-primary",
          {
            "opacity-50": isLoading,
          },
          className
        )}
        showSearch={true}
      />

      {isError && (
        <p className="mt-1 text-sm text-red-500" role="alert">
          {getErrorDetails(errorData)?.message || "Error loading fines"}
        </p>
      )}

      {error && (
        <p className="mt-1 text-sm text-red-500" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default FineDropdownMenu;
